/**
 * YouTube's caps on a post's text, as the API's schema enforces them
 * (`connectors/youtube/connector.ts` in atlas). Kept free of assets so the
 * module, the compose fields and the unit test share one copy of each rule.
 */

/** Characters, as `String.length` counts them; the API trims before counting. */
export const YOUTUBE_TITLE_MAX_LENGTH = 100;
/** Bytes of UTF-8, not characters: a description with emoji or accents reaches it earlier. */
export const YOUTUBE_DESCRIPTION_MAX_BYTES = 5000;
/** YouTube's own count of the tags together, see `youtubeTagsLength`. */
export const YOUTUBE_TAGS_MAX_LENGTH = 500;

const ANGLE_BRACKETS = /[<>]/;
const encoder = new TextEncoder();

export const hasAngleBrackets = (text: string): boolean => ANGLE_BRACKETS.test(text);

export const utf8Length = (text: string): number => encoder.encode(text).length;

/**
 * How YouTube counts a video's tags against its cap: every tag's length, a
 * comma between tags, and quotes around a tag that contains a space. The
 * same formula as the API's `withinTagsLength`.
 */
export const youtubeTagsLength = (tags: readonly string[]): number =>
  tags.reduce((sum, tag) => sum + tag.length + (tag.includes(' ') ? 2 : 0), 0) +
  Math.max(tags.length - 1, 0);

/**
 * Why a post's text cannot be scheduled yet, or null. Mirrors the API's
 * content schema so a refusal shows under the form instead of as a 400.
 */
export const youtubeTextProblem = ({
  title,
  description,
  tags,
}: {
  title: string;
  description?: string;
  tags?: readonly string[];
}): string | null => {
  if (!title.trim()) return 'A title is required.';
  if (title.trim().length > YOUTUBE_TITLE_MAX_LENGTH) {
    return `The title is over ${YOUTUBE_TITLE_MAX_LENGTH.toLocaleString()} characters.`;
  }
  if (hasAngleBrackets(title)) return 'The title cannot contain < or >.';
  if (description !== undefined) {
    if (hasAngleBrackets(description)) return 'The description cannot contain < or >.';
    // The API trims before counting, so the gate measures the same payload.
    if (utf8Length(description.trim()) > YOUTUBE_DESCRIPTION_MAX_BYTES) {
      return `The description is over ${YOUTUBE_DESCRIPTION_MAX_BYTES.toLocaleString()} bytes.`;
    }
  }
  if (tags && youtubeTagsLength(tags) > YOUTUBE_TAGS_MAX_LENGTH) {
    return `The tags are over ${YOUTUBE_TAGS_MAX_LENGTH.toLocaleString()} characters in all.`;
  }
  return null;
};
